// src/components/my-journey/ArchetypeReveal.jsx
// Feature 3 — Archetype Cinematic Reveal
// Three-step animated reveal: Analysis → Typewriter → Celebration + particle burst

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLeagueData, useManagerContext } from '../../context/DataContext';

// ── Step constants ──────────────────────────────────────────────────────────
const STEP_IDLE = 'idle';
const STEP_ANALYSIS = 'analysis';
const STEP_TYPEWRITER = 'typewriter';
const STEP_CELEBRATION = 'celebration';

// ── Map archetype name → image path ─────────────────────────────────────────
const archetypeImagePath = (name) => {
  if (!name) return null;
  const slug = name.toLowerCase().replace(/\s+/g, '-');
  return `/images/character-classes/${slug}.jpg`;
};

// ── Archetype definitions ────────────────────────────────────────────────────
// What each archetype means and how a manager earns it.
const ARCHETYPE_DESCRIPTIONS = {
  'Captain Fantastic':      "The league's sharpest armband caller. You picked the right captain more consistently than anyone — when it mattered most, your gut (or your spreadsheet) delivered.",
  'The Armband Whisperer':  "You rarely missed with the captaincy. Your points-missed percentage was among the lowest in the league, meaning your captain choices were almost always on the money.",
  'Warren Buffett':         "Your squad was worth more than everyone else's by the end of the season. You managed the transfer market like an investment portfolio — buying low, selling high.",
  'The Treasury Keeper':    "You consistently maintained one of the highest team values in the league. Patient in the market and never wasteful with your budget.",
  'King of The Hill':       "You finished at the top of the league table. The best manager this season — full stop.",
  'The Bridesmaid':         "Always near the summit but never quite champion. You were a consistent top performer throughout the season, but glory remained just out of reach.",
  'The Silver Knight':      "A reliable top-tier finisher. You were in the upper echelon of the league table from start to finish — one of the season's most dependable managers.",
  'The Dark Horse':         "You lurked near the bottom of the table overall, but you had gameweeks where you were the best in the league. Don't let anyone underestimate you.",
  'The Slump King':         "You endured the longest sustained below-average run in the league. It was a brutal stretch — but surviving it is a badge of honour in itself.",
  'The Battle-Scarred':     "You went through a significant rough patch this season. Weeks of below-average returns tested your resolve, but you came out the other side.",
  'The Glory Hunter':       "You went on the longest above-average scoring streak in the league. When you were hot, you were untouchable — week after week of outperforming.",
  'The Grinder':            "You hit a wall and came back fighting. Your recovery after a slump was one of the most impressive in the league — resilience is your superpower.",
  'The Closer':             "You saved your best for last. Your points haul in the final gameweeks was among the highest in the league — you peak when it matters most.",
  'The Endgame Tactician':  "You climbed more places in the final five gameweeks than almost anyone else. A masterclass in finishing — you play chess while others play draughts.",
  'The Fortune Teller':     "The luckiest manager in the league this season. Your players returned, your bench covered, your chips landed — the FPL gods smiled upon you.",
  'The Unlucky General':    "You deserved better. Your luck score was the lowest in the league — injuries, blanks, and bad timing conspired against you at every turn.",
  'The Risk Taker':         "You backed yourself with differentials more than almost anyone. High ownership players? Not your style. You went your own way — and it paid off.",
  'The Maverick':           "You operate completely outside the template. Your squad looks like no one else's, and you wouldn't have it any other way. The ultimate contrarian.",
  'The Steadfast':          "Rock-solid consistency, week in, week out. Your scores barely fluctuated — no disasters, no flukes. Just reliable, disciplined FPL management.",
  'The Wildborn':           "Your season was a rollercoaster. Massive highs, painful lows, and very little in between. Unpredictable, chaotic, and never boring.",
  'The Chip Virtuoso':      "You timed your chips better than almost anyone in the league. Triple Captains, Bench Boosts, Wildcards — you played them when the stars aligned.",
  'The Hive Mind':          "You trusted the template. High-ownership players, popular captains, safe transfers. You played the percentages and let the crowd do the thinking.",
  'The Equilibrium':        "Steady in the mid-table, season long. Not flashy, not struggling — just solid, reliable FPL management that kept you comfortably placed.",
  'The Journeyman':         "You finished right in the middle of the pack. The perfectly average FPL manager — and in a competitive league, that's harder than it sounds.",
  'The Tinkerer':           "You never stopped tweaking. Multiple transfers, constant rotation, always chasing the next fix. The most active manager in the market.",
  'The Rogue Scout':        "You found points others missed. Your differential picks hauled while everyone else blanked — you have an eye for talent the template ignores.",
  'The Chaos Mage':         "Your gameweek scores were all over the place — huge one week, quiet the next. Impossible to predict, dangerous on a good day, frustrating on a bad one.",
  'The Tactician':          "You approach FPL with a plan. Strategic team selection, thoughtful transfers, and a longer-term view than most managers in your league.",
  'The Adventurer':         "You're still finding your FPL identity. Every season teaches you something new, and your passion for the game is what drives you forward.",
  // Additional archetypes from the image folder
  'The Oracle':             "You called it before anyone else did. Whether it was a breakout player or a fixture swing, your foresight consistently put you ahead of the curve.",
  'The Phoenix':            "You rose from the ashes. After a disastrous run, you rebuilt and came back stronger — a comeback story the league will remember.",
  'The Guardian':           "A defensive mastermind. Your backline was the foundation of your season — when your defence kept clean sheets, your gameweeks shone.",
  'The Star-Crossed':       "So close, so often. You had the right instincts but the wrong timing — the kind of manager who sells the player the week before he scores a hat-trick.",
  'The Soothsayer':         "An FPL visionary. You anticipated trends, rotated smartly, and planned transfers weeks in advance. The long game is your natural habitat.",
  'The Favored One':        "Fortune favoured you this season in ways that went beyond mere luck — the right players, the right weeks, the right calls all aligned in your favour.",
  'The Team Zealot':        "Fiercely loyal to your club's players. You ride with your team through thick and thin, and when they perform, your FPL team performs with them.",
};

const archetypeDescription = (name) => ARCHETYPE_DESCRIPTIONS[name] || "A unique manager who defies easy classification. Your season carved its own path.";

// ── Typewriter component — character-by-character reveal via framer-motion ──
const Typewriter = ({ text, onComplete }) => {
  const [displayed, setDisplayed] = useState('');
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setDisplayed('');
    let index = 0;
    // ~50ms per character — adjust for feel
    intervalRef.current = setInterval(() => {
      index += 1;
      setDisplayed(text.slice(0, index));
      if (index >= text.length) {
        clearInterval(intervalRef.current);
        // Notify parent after a brief hold (1.5s per spec).
        // Store the timeout ID so it can be cleared on unmount.
        timeoutRef.current = setTimeout(onComplete, 1500);
      }
    }, 50);
    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timeoutRef.current);
    };
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span>
      {displayed}
      {displayed.length < text.length && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          style={{ color: 'var(--c-turquoise)' }}
        >
          |
        </motion.span>
      )}
    </span>
  );
};

// ── Particle burst — reuses confetti pattern from LeagueAwards.jsx ──────────
// Uses --q-turquoise and --q-blue as per spec
const ParticleBurst = () => {
  const colours = ['#44d5a3', '#3f69ae', '#44b6c5', '#44d5a3', '#3f69ae'];

  // Particle config is generated once via useMemo so Math.random() is not called
  // on every re-render — fixes Issue 5 (random values in render body).
  const particleConfigs = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) => ({
        colour: colours[i % colours.length],
        size: Math.random() * 8 + 4,
        left: `${Math.random() * 100}%`,
        delay: Math.random() * 0.6,
        duration: Math.random() * 2 + 1.5,
        isCircle: Math.random() > 0.5,
        animateTop: `${80 + Math.random() * 30}%`,
        animateRotate: Math.random() * 720 - 360,
        animateX: (Math.random() - 0.5) * 160,
      })),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {particleConfigs.map((p, i) => (
        <motion.div
          key={i}
          initial={{ top: '-5%', opacity: 1, rotate: 0, x: 0 }}
          animate={{
            top: p.animateTop,
            opacity: 0,
            rotate: p.animateRotate,
            x: p.animateX,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeIn',
          }}
          style={{
            position: 'absolute',
            left: p.left,
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.colour,
            borderRadius: p.isCircle ? '50%' : '2px',
          }}
        />
      ))}
    </div>
  );
};

// ── Progress bar — fills with framer-motion width animation ─────────────────
const ProgressBar = ({ label, delay, colour }) => {
  // Stable random width — computed once per mount, not on every re-render
  const targetWidth = useMemo(() => `${55 + Math.random() * 45}%`, []);

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span style={{ color: 'var(--c-grey)' }}>{label}</span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: 'var(--c-surface-3)' }}
      >
        <motion.div
          className="h-full rounded-full"
          initial={{ width: '0%' }}
          animate={{ width: targetWidth }}
          transition={{ duration: 1.2, delay, ease: 'easeOut' }}
          style={{ backgroundColor: colour }}
        />
      </div>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const ArchetypeReveal = () => {
  const { data } = useLeagueData();
  const { selectedManager } = useManagerContext();
  const [step, setStep] = useState(STEP_IDLE);
  const [hasRevealed, setHasRevealed] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const timerRefs = useRef([]);

  // ── Data access ──────────────────────────────────────────────────────────
  // The Player Archetypes sheet stores archetypes in three separate columns:
  // Primary_Archetype, Secondary_Archetype, Tertiary_Archetype
  const archetypeRow = data?.['Player Archetypes']?.find(
    (r) => r.Manager === selectedManager
  );
  const rawArchetypes = [
    archetypeRow?.Primary_Archetype,
    archetypeRow?.Secondary_Archetype,
    archetypeRow?.Tertiary_Archetype,
  ].filter(Boolean);
  const primaryArchetype = rawArchetypes[0] || null;

  const badgeRow = data?.['Player Badges']?.find(
    (r) => r.Manager === selectedManager
  );
  const topBadge = badgeRow?.Top_Badge || null;
  const badgeCount = badgeRow?.Badge_Count || 0;

  // ── Cleanup timers on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => timerRefs.current.forEach(clearTimeout);
  }, []);

  // ── Orchestrate the step sequence ────────────────────────────────────────
  const startReveal = useCallback(() => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
    setHasRevealed(true);
    setStep(STEP_ANALYSIS);

    // Step 2 — typewriter after 2s analysis (per spec)
    timerRefs.current.push(
      setTimeout(() => setStep(STEP_TYPEWRITER), 2000)
    );
    // Typewriter itself calls onComplete which triggers Step 3
  }, []);

  const handleTypewriterComplete = useCallback(() => {
    setStep(STEP_CELEBRATION);
  }, []);

  const replayReveal = useCallback(() => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
    setStep(STEP_IDLE);
    // Brief reset pause before replay
    timerRefs.current.push(setTimeout(startReveal, 100));
  }, [startReveal]);

  if (!selectedManager || !data) return null;

  // ── Idle trigger button ───────────────────────────────────────────────────
  if (step === STEP_IDLE) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
      >
        {/* Header */}
        <div
          className="px-5 py-4 flex items-center gap-3"
          style={{ borderBottom: '1px solid var(--c-border)', backgroundColor: 'var(--c-surface-1)' }}
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: 'rgba(68,213,163,0.15)',
              border: '1px solid rgba(68,213,163,0.3)',
            }}
          >
            <Award size={18} style={{ color: 'var(--c-turquoise)' }} />
          </div>
          <div>
            <h3 className="font-bold text-sm text-white">Your Manager Archetype</h3>
            <p className="text-xs" style={{ color: 'var(--c-grey)' }}>
              Discover the character class you earned this season
            </p>
          </div>
        </div>

        <div className="p-5 flex flex-col items-center gap-4 text-center">
          {primaryArchetype ? (
            <>
              <p className="text-sm" style={{ color: 'var(--c-grey)' }}>
                {hasRevealed
                  ? 'Watch the reveal again anytime.'
                  : "We've analysed your entire season — ready to see who you are?"}
              </p>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                onClick={startReveal}
                className="px-6 py-2.5 rounded-full text-sm font-semibold text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--c-blue) 0%, var(--c-cyan) 100%)',
                  border: '1px solid rgba(68,182,197,0.4)',
                }}
              >
                {hasRevealed ? 'Replay reveal' : 'Reveal my archetype'}
              </motion.button>
            </>
          ) : (
            <p className="text-xs italic" style={{ color: 'var(--c-grey)' }}>
              No archetype data available for this manager.
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  // ── Active reveal panel ────────────────────────────────────────────────────
  return (
    <div
      className="rounded-xl overflow-hidden relative"
      style={{
        backgroundColor: '#000006',
        border: '1px solid var(--c-border)',
        minHeight: '280px',
      }}
    >
      {/* ── Step 1 — Analysis (0–2s) ──────────────────────────────────────────── */}
      <AnimatePresence>
        {step === STEP_ANALYSIS && (
          <motion.div
            key="analysis"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-6 p-8"
          >
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-base font-semibold text-white text-center"
            >
              Analysing your season…
            </motion.p>

            <div className="w-full max-w-xs space-y-3">
              <ProgressBar
                label="Transfer Activity"
                delay={0.1}
                colour="var(--c-blue)"
              />
              <ProgressBar
                label="Captain Strategy"
                delay={0.4}
                colour="var(--c-turquoise)"
              />
              <ProgressBar
                label="Differential Play"
                delay={0.7}
                colour="var(--c-cyan)"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Step 2 — Typewriter reveal (2s) ──────────────────────────────────── */}
      <AnimatePresence>
        {step === STEP_TYPEWRITER && (
          <motion.div
            key="typewriter"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8"
            style={{ background: 'rgba(0,0,6,0.95)' }}
          >
            <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--c-grey)' }}>
              Your primary archetype is
            </p>
            <p
              className="text-3xl font-black text-center"
              style={{ color: 'var(--c-turquoise)', minHeight: '2.5rem' }}
            >
              <Typewriter
                text={primaryArchetype || 'The Adventurer'}
                onComplete={handleTypewriterComplete}
              />
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Step 3 — Celebration with carousel ───────────────────────────────── */}
      <AnimatePresence>
        {step === STEP_CELEBRATION && (
          <motion.div
            key="celebration"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="relative flex flex-col items-center justify-center gap-4 p-6 pt-8 pb-8"
          >
            {/* Particle burst — only on first card */}
            {carouselIndex === 0 && <ParticleBurst />}

            {/* Carousel header */}
            {rawArchetypes.length > 1 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="text-xs uppercase tracking-widest relative z-10"
                style={{ color: 'var(--c-grey)' }}
              >
                {carouselIndex + 1} of {rawArchetypes.length} archetypes
              </motion.p>
            )}

            {/* Archetype card — carousel slide */}
            {/* Flip card container */}
            <AnimatePresence mode="wait">
              <motion.div
                key={carouselIndex}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="relative z-10 w-full max-w-xs"
                style={{ perspective: '1000px' }}
              >
                {/* Inner — rotates on flip */}
                <motion.div
                  animate={{ rotateY: flipped ? 180 : 0 }}
                  transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
                  style={{ transformStyle: 'preserve-3d', position: 'relative' }}
                  className="cursor-pointer"
                  onClick={() => setFlipped((f) => !f)}
                  title={flipped ? 'Click to flip back' : 'Click to reveal description'}
                >
                  {/* ── Front face — character image ── */}
                  <div
                    className="rounded-xl overflow-hidden w-full"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      border: `1px solid ${carouselIndex === 0 ? 'rgba(68,213,163,0.5)' : 'rgba(63,105,174,0.4)'}`,
                      boxShadow: carouselIndex === 0 ? '0 0 40px rgba(68,213,163,0.2)' : '0 0 20px rgba(63,105,174,0.15)',
                    }}
                  >
                    <div className="relative w-full" style={{ aspectRatio: '4/3' }}>
                      <img
                        src={archetypeImagePath(rawArchetypes[carouselIndex])}
                        alt={rawArchetypes[carouselIndex]}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div
                        className="absolute inset-0"
                        style={{ background: 'linear-gradient(to top, rgba(0,0,6,0.95) 0%, rgba(0,0,6,0.15) 60%, transparent 100%)' }}
                      />
                      <span
                        className="absolute top-3 left-3 text-xs uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold"
                        style={{
                          backgroundColor: carouselIndex === 0 ? 'rgba(68,213,163,0.85)' : 'rgba(63,105,174,0.85)',
                          color: '#000',
                        }}
                      >
                        {carouselIndex === 0 ? 'Primary' : `#${carouselIndex + 1}`}
                      </span>
                      {/* Tap hint */}
                      <span
                        className="absolute bottom-14 right-3 text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'var(--c-grey)', border: '1px solid var(--c-border)' }}
                      >
                        tap to flip
                      </span>
                    </div>
                    <div className="p-4 text-center" style={{ backgroundColor: 'rgba(0,0,6,0.95)' }}>
                      <h4 className="text-xl font-black text-white mb-1">{rawArchetypes[carouselIndex]}</h4>
                      {carouselIndex === 0 && topBadge && (
                        <p className="text-xs mt-1" style={{ color: 'var(--c-yellow)' }}>
                          {topBadge}{badgeCount > 1 ? ` +${badgeCount - 1} more` : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ── Back face — definition ── */}
                  <div
                    className="absolute inset-0 rounded-xl flex flex-col items-center justify-center p-6 text-center"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      background: 'linear-gradient(135deg, var(--c-surface-1) 0%, rgba(63,105,174,0.25) 100%)',
                      border: `1px solid ${carouselIndex === 0 ? 'rgba(68,213,163,0.5)' : 'rgba(63,105,174,0.4)'}`,
                    }}
                  >
                    <Award size={28} className="mb-3 flex-shrink-0" style={{ color: carouselIndex === 0 ? 'var(--c-turquoise)' : 'var(--c-blue)' }} />
                    <h4 className="text-base font-black text-white mb-3">{rawArchetypes[carouselIndex]}</h4>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--c-grey)' }}>
                      {archetypeDescription(rawArchetypes[carouselIndex])}
                    </p>
                    <p className="text-xs mt-4" style={{ color: 'var(--c-border)' }}>tap to flip back</p>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>

            {/* Carousel nav buttons */}
            {rawArchetypes.length > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className="relative z-10 flex items-center gap-3"
              >
                <button
                  onClick={() => { setCarouselIndex((i) => Math.max(0, i - 1)); setFlipped(false); }}
                  disabled={carouselIndex === 0}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid var(--c-border)',
                    opacity: carouselIndex === 0 ? 0.3 : 1,
                  }}
                >
                  <ChevronLeft size={14} style={{ color: 'var(--c-grey)' }} />
                </button>
                {rawArchetypes.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCarouselIndex(i)}
                    className="w-2 h-2 rounded-full transition-all"
                    style={{
                      backgroundColor: i === carouselIndex ? 'var(--c-turquoise)' : 'var(--c-border)',
                      transform: i === carouselIndex ? 'scale(1.4)' : 'scale(1)',
                    }}
                  />
                ))}
                <button
                  onClick={() => { setCarouselIndex((i) => Math.min(rawArchetypes.length - 1, i + 1)); setFlipped(false); }}
                  disabled={carouselIndex === rawArchetypes.length - 1}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid var(--c-border)',
                    opacity: carouselIndex === rawArchetypes.length - 1 ? 0.3 : 1,
                  }}
                >
                  <ChevronRight size={14} style={{ color: 'var(--c-grey)' }} />
                </button>
              </motion.div>
            )}

            {/* Replay button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.4 }}
              onClick={() => { setCarouselIndex(0); setFlipped(false); replayReveal(); }}
              className="relative z-10 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs"
              style={{
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid var(--c-border)',
                color: 'var(--c-grey)',
              }}
            >
              <RotateCcw size={11} />
              Replay reveal
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArchetypeReveal;
